import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { Flag, Send, Add, Clear } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Checkbox, Chip, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { Column } from 'react-table';
import { useQueryParams } from 'use-query-params';
import XLSX from 'xlsx-js-style';

import { batchCreateFlags, fetchVerboseCandidates } from 'api';
import { addCandidateToTranche } from 'api/tranches';
import { LinkButton } from 'components';
import { AddFlag } from 'components/AddFlag';
import { Table } from 'components/Table';
import { CheckboxWrapper } from 'components/Table/styleComponents';
import { colors } from 'config';
import {
  convertToDateShort,
  flagTypes,
  flagTypeToLabel,
  getPageCount,
  sivStatuses,
} from 'helpers';
import {
  DelimitedNumericArrayParam,
  DateParam,
  StringParam,
  NumberParam,
  BooleanParam,
  createEnumArrayParam,
  createEnumParam,
} from 'helpers/useQueryParams';
import { useUrlPagination, useControlledPagination } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import {
  AdminCandidateTableProps,
  ContractOwner,
  FlagType,
  OrganizationKnownStatus,
  ReviewStatus,
  SivStatus,
  TrancheStatus,
} from 'types';

import {
  CellWrapper,
  DashboardWrapper,
  NullValueMarker,
} from '../styledComponents';
import { AdminCandidateTableSidebar } from './AdminCandidateTableSidebar';

const filterConfig = {
  organization_id: DelimitedNumericArrayParam,
  tranche_id: DelimitedNumericArrayParam,
  sequence_no: DelimitedNumericArrayParam,
  birthdate_min: DateParam,
  birthdate_max: DateParam,
  candidate_id: DelimitedNumericArrayParam,
  last_reviewed_date_min: StringParam,
  last_reviewed_date_max: StringParam,
  fuzzy: BooleanParam,
  review_status_includes: createEnumArrayParam<ReviewStatus>([
    'pending_user_review',
    'review_in_progress',
    'did_not_work_insufficient_info',
    'did_not_work_certain',
    'reviewed_and_verified',
    'unable_to_review',
  ]),
  review_status_only: createEnumParam<ReviewStatus>([
    'pending_user_review',
    'review_in_progress',
    'did_not_work_insufficient_info',
    'did_not_work_certain',
    'reviewed_and_verified',
    'unable_to_review',
  ]),
  contract_owner_includes: createEnumArrayParam<ContractOwner>([
    'dod_contract',
    'not_dod_contract',
    'insufficient_information',
  ]),
  contract_owner_only: createEnumParam<ContractOwner>([
    'dod_contract',
    'not_dod_contract',
    'insufficient_information',
  ]),
  flag_types_include: createEnumArrayParam<FlagType>(flagTypes),
  flag_types_exclude: createEnumArrayParam<FlagType>(flagTypes),
  include_siv_ineligible: BooleanParam,
  validated_work_days_min: NumberParam,
  validated_work_days_max: NumberParam,
  has_derogs: BooleanParam,
  has_for_cause: BooleanParam,
  firstname: StringParam,
  lastname: StringParam,
  birthdate: StringParam,
  tazkera: StringParam,
  case_number: StringParam,
  passport_number: StringParam,
  employer_known: createEnumParam<OrganizationKnownStatus>([
    'unknown',
    'known',
    'unknown_known',
  ]),
  organizationally_unreviewable: BooleanParam,
  tranche_status: createEnumParam<TrancheStatus>([
    'sent_to_state',
    'signature_pending',
    'active',
    'skipped',
  ]),
  supervisor_given_name: StringParam,
  supervisor_surname: StringParam,
  supervisor_email: StringParam,
  employer_email: StringParam,
  employer_occupation: StringParam,
  siv_status_id: createEnumArrayParam<SivStatus>(sivStatuses),
};

export const AdminCandidateTable = () => {
  const location = useLocation();
  const urlFrom = useMemo(
    () => location.pathname + location.search,
    [location.pathname, location.search],
  );
  const { setSnack } = useSnackContext();

  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const [filters, setFilters] = useQueryParams(filterConfig, {
    updateType: 'replaceIn',
  });

  const config = useMemo(() => {
    const { case_number, firstname, lastname, passport_number, ...rest } =
      filters;
    const firstnameTrimmed = firstname?.trim();
    const lastnameTrimmed = lastname?.trim();
    const fuzzy =
      filters.firstname ||
      filters.lastname ||
      filters.case_number ||
      filters.passport_number
        ? true
        : undefined;
    return {
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
        firstname: firstnameTrimmed,
        lastname: lastnameTrimmed,
        case_number: case_number,
        passport_number: passport_number,
        ...rest,
        fuzzy,
      },
    };
  }, [controlledPageIndex, controlledRows, filters]);
  const { dataArray, dataLoaded, count, updateData } = useControlledPagination(
    fetchVerboseCandidates,
    config,
  );
  const [addFlagModal, setAddFlagModal] = useState(false);
  const { updateControlledPageIndex } = useUrlPagination();

  const [checkedCandidates, setCheckedCandidates] = useState<Set<number>>(
    new Set(),
  );

  const [data, setData] = useState(dataArray);

  const handleAddToExport = useMemo(() => {
    const addData = () => {
      setData([...data, ...dataArray]);
    };
    return addData;
  }, [data, dataArray]);

  const handleClearExportList = useMemo(() => {
    const removeData = () => {
      setData([]);
    };
    return removeData;
  }, []);

  const flattenArray = () =>
    data.flatMap(({ organizations, ...rest }) =>
      organizations.map((o) => ({ ...rest, ...o })),
    );
  const result = flattenArray();

  const handleExportFile = useMemo(() => {
    const handleExport = () => {
      const workBook = XLSX.utils.book_new();
      const workSheet = XLSX.utils.json_to_sheet(result);
      XLSX.utils.book_append_sheet(workBook, workSheet, 'Applicants_Info');
      XLSX.writeFile(
        workBook,
        'SIV_Applicants_' + convertToDateShort(Date()) + '.xlsx',
      );
    };
    return handleExport;
  }, [result]);
  const handleAddFlags = async (
    candidateIds: Array<number>,
    flagType: FlagType,
    message: string,
  ) => {
    const { data } = await batchCreateFlags(candidateIds, {
      type: flagType,
      message: message,
    });
    data?.forEach((candidate) => {
      updateData(candidate);
    });
    setCheckedCandidates(new Set());
  };
  useEffect(() => {
    try {
      setCheckedCandidates(
        new Set(JSON.parse(localStorage.getItem('checkedCandidates') ?? '[]')),
      );
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'warning',
        open: true,
        message: 'Failed to parse local storage',
        error: e as Error,
      }));
    }
    return () => {
      setCheckedCandidates(new Set());
    };
  }, [setSnack]);

  useEffect(() => {
    setCheckedCandidates(new Set());

    return () => {
      setCheckedCandidates(new Set());
    };
  }, [dataArray]);
  const globalFiltersApplied = useMemo(
    () => Object.entries(filters).some(([, value]) => value !== undefined),
    [filters],
  );

  // update and return to first page when filter is applied
  const wrapSetCandidateFilter = useCallback(
    (arg: Parameters<typeof setFilters>[0]) => {
      setFilters(arg);
      updateControlledPageIndex(1);
    },
    [setFilters, updateControlledPageIndex],
  );

  // used to calculate the table's checkbox location
  const [selectAllOffset, setSelectAllOffset] = useState(135);
  useLayoutEffect(() => {
    const table = document.querySelector('.table');
    if (table) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const inspect = entry.target.querySelector('.inspect:first-child');
          const checkbox = entry.target.querySelector('.checkbox:first-child');
          if (inspect && checkbox) {
            const inspectRect = inspect.getBoundingClientRect();
            const checkboxRect = checkbox.getBoundingClientRect();
            setSelectAllOffset(checkboxRect.x - inspectRect.x);
          }
        }
      });
      resizeObserver.observe(table);
      return () => resizeObserver.unobserve(table);
    }
  }, [dataArray]);

  const columns = useMemo<Array<Column<AdminCandidateTableProps>>>(
    () => [
      {
        id: 'inspect',
        Header: () => {
          return (
            <CellWrapper aria-label='Inspect candidate'>
              <p />
            </CellWrapper>
          );
        },
        width: 100,
        Cell: (row: any) => {
          return (
            <CellWrapper className='inspect'>
              <LinkButton
                aria-label={
                  'Inspect candidate ' +
                  row.row.original.firstname +
                  ' ' +
                  row.row.original.lastname
                }
                // disabled={!row.row.original.is_reviewable}
                variant='text'
                sx={{ width: '70%' }}
                to={{
                  pathname: `reviewed_candidates/${row.row.original.id}`,
                  state: {
                    from: urlFrom,
                  },
                }}
              >
                INSPECT
              </LinkButton>
            </CellWrapper>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'checked',
        Header: () => {
          return (
            <CellWrapper aria-label='Select candidate to add flags'>
              <p />
            </CellWrapper>
          );
        },
        width: 50,
        Cell: (row: any) => {
          const { id, tranche_id, is_reviewable } = row.row.original;
          const checked = checkedCandidates.has(id);
          return (
            <CellWrapper>
              <CheckboxWrapper>
                <Checkbox
                  id='add_flag'
                  className='checkbox'
                  checked={checked}
                  disabled={Number.isInteger(tranche_id) || !is_reviewable}
                  size='small'
                  role='checkbox'
                  aria-checked
                  aria-disabled
                  inputProps={{
                    'aria-label':
                      'add flag to ' +
                      row.row.original.firstname +
                      ' ' +
                      row.row.original.lastname,
                  }}
                  onChange={(e) => {
                    e.preventDefault();
                    setCheckedCandidates(
                      (prev) =>
                        new Set(
                          checked
                            ? (prev.delete(id), prev.values())
                            : prev.add(id),
                        ),
                    );
                  }}
                />
              </CheckboxWrapper>
            </CellWrapper>
          );
        },
      },
      {
        id: 'firstname',
        Header: 'Given Name',
        accessor: 'firstname',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'lastname',
        Header: 'Family Name',
        accessor: 'lastname',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'siv_status',
        Header: 'SIV Status',
        accessor: 'siv_status',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'birthdate',
        Header: 'DOB',
        accessor: (row) => convertToDateShort(row.birthdate),
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'siv_id',
        Header: 'NVCSIV ID',
        accessor: 'case_number',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'passport_number',
        Header: 'Passport',
        accessor: 'passport_number',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'tazkera',
        Header: 'Tazkera',
        accessor: 'tazkera',
        Cell: (row: any) => {
          const { tazkera } = row.row.original;
          return tazkera && tazkera !== 'null' ? tazkera : <NullValueMarker />;
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'employer_group',
        Header: 'Employer Name',
        accessor: 'organizations',
        Cell: (row) => {
          const { organizations } = row.row.original;
          if (organizations.length === 0) {
            return (
              <CellWrapper>
                <NullValueMarker />
              </CellWrapper>
            );
          }
          return (
            <Typography variant='body1'>
              {organizations.map((o) => o.name).join(' // ')}
            </Typography>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'flags',
        Header: 'Flags',
        accessor: 'flags',
        Cell: (row) => {
          const { flags } = row.row.original;

          return flags.length === 0 ? (
            <NullValueMarker />
          ) : (
            flags.map(({ type }, index) => (
              <Chip
                key={type || `unknown_type_${index}`}
                label={flagTypeToLabel(type)}
              />
            ))
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'tranche',
        Header: 'Tranche',
        accessor: 'tranche',
        Cell: (row: any) => {
          const [loading, setLoading] = useState(false);
          const { tranche, id, validated_work_days, is_reviewable } =
            row.row.original;
          return tranche ? (
            <Typography variant='body1'>
              {' '}
              {`tranche ${tranche.sequence_no}`}
            </Typography>
          ) : (
            <LoadingButton
              aria-busy
              aria-label='Loading button'
              loading={loading}
              variant='outlined'
              disabled={!(validated_work_days > 0) || !is_reviewable}
              onClick={async () => {
                setLoading(true);
                try {
                  const { data } = await addCandidateToTranche(id);
                  updateData(data);
                } catch (e) {
                  setSnack((props) => ({
                    ...props,
                    severity: 'error',
                    open: true,
                    message: 'Failed to Add Candidate to Tranche',
                    error: e as Error,
                  }));
                } finally {
                  setLoading(false);
                }
              }}
            >
              Add
            </LoadingButton>
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
    ],
    [updateData, checkedCandidates, setSnack, urlFrom],
  );

  const tableControls = useMemo(() => {
    return {
      ml: selectAllOffset,
      selectAll: (props: { action: boolean; entities: Array<any> }) => {
        const { action, entities } = props;
        if (action !== null) {
          action
            ? setCheckedCandidates(
                (prev) =>
                  new Set(
                    entities
                      .filter(({ tranche_id }) => !Number.isInteger(tranche_id))
                      .reduce((prev, { id }) => prev.add(id), prev),
                  ),
              )
            : setCheckedCandidates(new Set());
        }
      },
      controls: [
        {
          key: 'add_flag',
          text: 'Add Flag',
          icon: <Flag />,
          disabled: !checkedCandidates.size,
          click: () => setAddFlagModal(true),
        },
        {
          key: 'clear_export_file',
          text: 'Empty Export File',
          icon: <Clear />,
          disabled: false,
          click: () => handleClearExportList(),
        },
        {
          key: 'add_export_candidates',
          text: 'Add Candidates To Export',
          icon: <Add />,
          disabled: false,
          click: () => handleAddToExport(),
        },
        {
          key: 'export_file',
          text: 'Export File',
          icon: <Send />,
          disabled: false,
          click: () => handleExportFile(),
        },
      ],
      selected: checkedCandidates,
    };
  }, [
    selectAllOffset,
    checkedCandidates,
    handleAddToExport,
    handleExportFile,
    handleClearExportList,
  ]);

  useEffect(() => {
    localStorage.setItem(
      'checkedCandidates',
      JSON.stringify(Array.from(checkedCandidates.values())),
    );
    return () => {
      localStorage.setItem('ceckedCandidates', JSON.stringify([]));
    };
  }, [checkedCandidates]);

  return (
    <>
      <DashboardWrapper>
        <>
          <AdminCandidateTableSidebar
            setFilters={wrapSetCandidateFilter}
            filters={filters}
          />
          <Table<AdminCandidateTableProps>
            className='table'
            loading={!dataLoaded}
            count={count}
            rowProps={(row) => {
              return !row.is_reviewable
                ? {
                    style: {
                      backgroundColor: colors.uswds.orange10,
                      borderBottom: '1px solid white',
                    },
                  }
                : {};
            }}
            globalFiltersApplied={globalFiltersApplied}
            handleGlobalFilterClear={() => {
              // Reset filters in history state, these should match the filters set in filterConfig
              wrapSetCandidateFilter({
                organization_id: undefined,
                tranche_id: undefined,
                birthdate_min: undefined,
                birthdate_max: undefined,
                candidate_id: undefined,
                last_reviewed_date_min: undefined,
                last_reviewed_date_max: undefined,
                fuzzy: undefined,
                review_status_includes: undefined,
                review_status_only: undefined,
                contract_owner_includes: undefined,
                contract_owner_only: undefined,
                flag_types_include: undefined,
                flag_types_exclude: undefined,
                include_siv_ineligible: undefined,
                validated_work_days_min: undefined,
                validated_work_days_max: undefined,
                has_derogs: undefined,
                has_for_cause: undefined,
                firstname: undefined,
                lastname: undefined,
                birthdate: undefined,
                tazkera: undefined,
                case_number: undefined,
                employer_known: undefined,
                organizationally_unreviewable: undefined,
                tranche_status: undefined,
                supervisor_given_name: undefined,
                supervisor_surname: undefined,
                supervisor_email: undefined,
                employer_email: undefined,
                employer_occupation: undefined,
                siv_status_id: undefined,
                passport_number: undefined,
              });
            }}
            useTableOptions={{
              data: dataArray,
              columns: columns,
              manualPagination: true,
              pageCount: getPageCount(count, controlledRows),
              autoResetPage: false,
            }}
            tableControls={
              dataArray.length || tableControls.selected.size
                ? tableControls
                : undefined
            }
            counterClick={() => {
              wrapSetCandidateFilter({
                candidate_id: Array.from(checkedCandidates.values()),
              });
            }}
          />
        </>
      </DashboardWrapper>
      <AddFlag
        open={addFlagModal}
        candidateIds={Array.from(checkedCandidates)}
        handleAddFlags={handleAddFlags}
        handleClose={() => setAddFlagModal(false)}
      />
    </>
  );
};
